// «Elige el teléfono»: una tarjeta por modelo de teléfono (capacidad y color dentro) y una por kit.
// Tocar muestra el detalle; «Elegir» aparta el SKU (o cambia el equipo de la venta) y avanza.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { ArrowRight, PackageX, SearchX } from 'lucide-react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMXN } from '@/shared/lib/format/money';
import {
    useCatalog,
    useRenewLineReservation,
    useScoreConsultSilent,
    useSelectProduct,
    useWizardSession,
} from '../api/useWizard';
import { accountAlreadyOpen } from '../accountWait';
import { anchorLineOf } from '../buildSale';
import {
    cardCreditBubbles,
    cardCreditOf,
    sureKeepFinances,
    creditTermsFor,
    downPaymentNote,
    type LevelCreditTerms,
} from '../cardBenefits';
import {
    FILTROS_VACIOS as EMPTY_FILTERS,
    aplicarFiltros as applyFilters,
    financiersOf,
    marcasDe as brandsOf,
    terminosDe as termsOf,
    type CatalogFilterState,
} from '../catalogFilters';
import {
    creditPromotionRows,
    creditPromotionsForItem,
    type CreditPromotionRow,
} from '../creditPromotions';
import { IntentKeys } from '../intentKeys';
import { isTransientRequestError } from '../deviceEnrollment';
import { needsCatalogReload } from '../lineErrors';
import { forgetLinkedSku, peekLinkedSku } from '../linkedSku';
import { previousStepSlug, stepAfterCheckout } from '../steps';
import {
    catalogForSession,
    isSureKeepAvailable,
    isSureKeepConfirmed,
} from '../sureKeepAvailability';
import { isReservationLiveNow } from '../reservationClock';
import {
    PHONE_OFFER_FILTERS,
    phoneLineOf,
    phoneOffersOf,
    type PhoneOfferFilter,
} from '../productSuggestions';
import {
    LINE_CHANGE_ACTION_LABEL,
    RENEW_UNAVAILABLE_MESSAGE,
    lineChangeAction,
    renewFallbackOf,
    reservationErrorMessage,
    swapErrorMessage,
    type LineChangeAction,
} from '../reservationErrors';
import { CatalogToolbar } from '../components/CatalogToolbar';
import { LevelChip } from '../components/LevelChip';
import { ProductCard, ProductCardSkeleton } from '../components/ProductCard';
import { ModelCard } from '../components/ModelCard';
import {
    catalogEntries,
    type CatalogEntry,
    type ModelGroup,
} from '@/domains/sales/portal/catalog/lib/modelGroups';
import { ProductDetailCard } from '../components/ProductDetailCard';
import { KitContentsModal } from '../components/KitContentsModal';
import { kitComponentCount } from '../kitContents';
import { PromoShortcutButton } from '../components/PromoShortcutButton';
import { customerFirstName } from '../referrals';
import { SaveAndExitButton, WizardBackLink } from '../components/WizardFootNav';
import type {
    BenefitBubble,
    CardSaleBenefits,
    RenderAccountBonuses,
    SessionRefreshContext,
} from '../promotionSlots';
import type { CatalogItem, PlanLevel } from '../types';

const SKELETONS = 6;

/** Lugares para las piezas de Promociones (REQ-134); sin pieza no dejan hueco. */
export interface PhoneSelectSlots {
    /** Banner verde «Promos de venta»: las de categoría, contadas sobre los equipos de la pantalla. */
    salePromotionsBanner?: (items: readonly CatalogItem[]) => ReactNode;
    /** Aviso «Llegó recomendado»: quién lo recomendó y lo que gana el cliente. */
    referredByNotice?: (customerName: string) => ReactNode;
    /** Burbujas de promos propias y precio con descuento de cada tarjeta. */
    saleBenefits?: (item: CatalogItem) => CardSaleBenefits;
    /** Descuentos, bonos y regalos del detalle; los bonos se pintan con `renderBonuses`. */
    detailPromotions?: (
        item: CatalogItem,
        ctx: SessionRefreshContext,
        renderBonuses: RenderAccountBonuses,
    ) => ReactNode;
}

/** Atajo `/` para saltar al buscador, salvo que ya se esté escribiendo en un campo. */
function useSearchShortcut(searchRef: React.RefObject<HTMLInputElement | null>): void {
    useEffect(() => {
        const onKey = (e: KeyboardEvent): void => {
            if (e.key !== '/' || e.ctrlKey || e.metaKey) return;
            const active = document.activeElement;
            if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
            e.preventDefault();
            searchRef.current?.focus();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
        };
    }, [searchRef]);
}

export function PhoneSelectStep({
    sessionId,
    slots = {},
}: {
    sessionId: string;
    slots?: PhoneSelectSlots;
}) {
    const navigate = useNavigate();
    // `?sku=` de «Iniciar venta» en el catálogo del portal.
    const { sku: urlSku } = useSearch({ from: '/wizard' });
    // Sin `?sku=` en la URL, el que se ligó a la sesión al crearla; se consume una sola vez.
    const [storedSku] = useState(() => peekLinkedSku(sessionId));
    useEffect(() => {
        forgetLinkedSku(sessionId);
    }, [sessionId]);
    const linkedSku = urlSku ?? storedSku;
    const {
        data: session,
        refresh: refreshSession,
        refetch: refetchSession,
    } = useWizardSession(sessionId);
    const { data: rawCatalog, isLoading, isFetching, isError, refetch } = useCatalog(sessionId);
    const sureKeep = isSureKeepConfirmed(session);
    const catalog = useMemo(() => catalogForSession(rawCatalog, sureKeep), [rawCatalog, sureKeep]);
    // Las promos se muestran desde antes del score; solo un cliente que no califica las pierde.
    const notEligible = !isSureKeepAvailable(session);
    const promoCatalog = useMemo(
        () => catalogForSession(rawCatalog, !notEligible),
        [rawCatalog, notEligible],
    );
    const levelPending = !notEligible && !sureKeep;
    const selectProduct = useSelectProduct(sessionId);
    const renewReservation = useRenewLineReservation(sessionId);
    // Cubre toda la renovación, incluida la relectura y el apartado de respaldo.
    const renewingRef = useRef(false);
    const [renewing, setRenewing] = useState(false);
    const scoreConsultSilent = useScoreConsultSilent(sessionId);
    // Un solo apartado en vuelo: el doble clic en «Elegir» no aparta dos unidades.
    const reservingRef = useRef(false);
    const [intentKeys] = useState(() => new IntentKeys());

    // Score adelantado y silencioso: marca el catálogo con el nivel antes de la forma de pago.
    const scoreTriggeredRef = useRef(false);
    useEffect(() => {
        if (scoreTriggeredRef.current) return;
        if (!session || !accountAlreadyOpen(session.accountStatus) || session.loan?.level) return;
        scoreTriggeredRef.current = true;
        scoreConsultSilent.mutate(undefined, { onError: () => undefined });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- dispara una sola vez por montaje/condición
    }, [session]);

    const [offerFilter, setOfferFilter] = useState<PhoneOfferFilter>('ALL');
    const [filters, setFilters] = useState<CatalogFilterState>(EMPTY_FILTERS);
    const searchRef = useRef<HTMLInputElement>(null);
    useSearchShortcut(searchRef);
    // `undefined` = el vendedor no ha tocado nada y manda el deep-link o el teléfono de la venta.
    const [touchedSku, setTouchedSku] = useState<string | undefined>(undefined);
    const [kitContentsSku, setKitContentsSku] = useState<string | undefined>(undefined);

    // NOT_ELIGIBLE se trata como «nivel resuelto, sin nivel» para las promociones.
    const clientLevel: PlanLevel | null | undefined =
        session?.loan?.level === undefined
            ? undefined
            : session.loan.level === 'NOT_ELIGIBLE'
              ? null
              : session.loan.level;

    const items = useMemo(() => catalog?.items ?? [], [catalog]);
    const offers = useMemo(() => phoneOffersOf(items, 'ALL'), [items]);
    // La cuadrícula también lleva las variantes agotadas: se ven deshabilitadas en su tarjeta.
    const typeOffers = useMemo(
        () => phoneOffersOf(items, offerFilter, { keepSoldOutPhones: true }),
        [items, offerFilter],
    );
    const visible = useMemo(() => applyFilters(typeOffers, filters), [typeOffers, filters]);
    const entries = useMemo(() => catalogEntries(visible), [visible]);
    const totalEntries = useMemo(() => catalogEntries(typeOffers).length, [typeOffers]);
    // Variante que el vendedor dejó elegida en cada tarjeta, por modelo.
    const [variantChoice, setVariantChoice] = useState<Record<string, string>>({});
    // Las opciones del filtro salen de lo que muestra la cuadrícula, agotados incluidos.
    const gridOffers = useMemo(
        () => phoneOffersOf(items, 'ALL', { keepSoldOutPhones: true }),
        [items],
    );
    const brands = useMemo(() => brandsOf(gridOffers), [gridOffers]);
    const financiers = useMemo(() => financiersOf(gridOffers), [gridOffers]);
    const terms = useMemo(() => termsOf(filters.q), [filters.q]);
    const promoItems = useMemo(
        () => new Map((promoCatalog?.items ?? []).map((item) => [item.sku, item])),
        [promoCatalog],
    );
    /** El equipo como lo ven las promos: con SureKeep aunque el nivel no esté resuelto. */
    const promoItem = (item: CatalogItem): CatalogItem => promoItems.get(item.sku) ?? item;
    const promoLevel = sureKeep ? clientLevel : undefined;
    const scopeWide = useMemo(() => rawCatalog?.scopeWidePromotions ?? [], [rawCatalog]);
    const creditRows = useMemo(
        () => creditPromotionRows(scopeWide, offers, promoLevel),
        [scopeWide, offers, promoLevel],
    );
    const creditRowsFor = (item: CatalogItem): CreditPromotionRow[] =>
        sureKeepFinances(promoItem(item)) ? creditPromotionsForItem(creditRows, item) : [];
    const creditTerms: LevelCreditTerms = {
        ...(catalog?.minDownPaymentPct !== undefined && { minPct: catalog.minDownPaymentPct }),
        ...(catalog?.creditCapCents !== undefined && { capCents: catalog.creditCapCents }),
    };

    const sessionPhoneLine = phoneLineOf(session);
    const sessionPhoneSku = sessionPhoneLine?.sku;
    // Un ?sku= de accesorio o de kit sin teléfono no es un equipo de esta pantalla: se ignora.
    const offered = (sku: string | undefined): string | undefined =>
        sku !== undefined && offers.some((i) => i.sku === sku) ? sku : undefined;
    const pickedSku = touchedSku ?? offered(linkedSku) ?? offered(sessionPhoneSku);
    const picked = offers.find((i) => i.sku === pickedSku) ?? null;
    /** La variante de la tarjeta: la que eligió el vendedor, la del detalle o la por defecto. */
    const variantOf = (group: ModelGroup): CatalogItem =>
        group.variants.find((v) => v.sku === variantChoice[group.key]) ??
        group.variants.find((v) => v.sku === pickedSku) ??
        group.defaultVariant;
    const changeVariant = (group: ModelGroup, item: CatalogItem): void => {
        setVariantChoice((current) => ({ ...current, [group.key]: item.sku }));
        setTouchedSku(item.sku);
    };
    const pickedBenefits = picked ? slots.saleBenefits?.(promoItem(picked)) : undefined;
    const pickedDiscount = pickedBenefits?.discountedPriceCents ?? null;
    const pickedCashPrice = pickedBenefits?.cashPriceCents;
    const pickedPriceCents =
        picked === null
            ? 0
            : pickedDiscount !== null && pickedDiscount < picked.cashPriceCents
              ? pickedDiscount
              : picked.cashPriceCents;
    const pickedCredit = picked ? cardCreditOf(picked, pickedPriceCents, creditTerms) : null;
    const pickedDownPayment =
        pickedCredit?.kind === 'surekeep' && pickedCredit.downPayment
            ? {
                  cents: pickedCredit.downPayment.cents,
                  note: downPaymentNote(
                      pickedCredit.downPayment,
                      pickedPriceCents,
                      clientLevel,
                      picked ? creditTermsFor(picked, creditTerms) : creditTerms,
                  ),
              }
            : null;
    const busy = selectProduct.isPending || renewReservation.isPending || renewing;
    const kitInModal = offers.find((i) => i.sku === kitContentsSku) ?? null;
    const isPickedKit = picked?.category === 'BUNDLE';
    const refreshContext: SessionRefreshContext = {
        refreshSession: async () => {
            await refreshSession();
        },
    };

    const goToBuildSale = (): void => {
        void navigate({
            to: '/wizard/s/$sessionId/$step',
            params: { sessionId, step: 'build-sale' },
        });
    };

    /** Avisa el error de apartar y relee la venta; con `replaces` el equipo anterior se queda. */
    const reportLineError = (error: unknown, item: CatalogItem, replaces?: string): void => {
        const message =
            replaces !== undefined ? swapErrorMessage(error) : reservationErrorMessage(error);
        const action = lineChangeAction(error);
        if (action === undefined) toast.error(message);
        else toast.error(message, { action: toastAction(action, item) });
        void refreshSession();
        if (needsCatalogReload(error)) void refetch();
    };

    /** Aparta el equipo y avanza; con `replaces` cambia esa línea en la misma llamada (REQ-84). */
    const reserve = async (item: CatalogItem, replaces?: string): Promise<void> => {
        if (reservingRef.current) return;
        reservingRef.current = true;
        // El reintento manual del mismo equipo reusa la llave: el silo no aparta dos veces.
        const intent = `${item.sku}|${replaces ?? ''}`;
        try {
            await selectProduct.mutateAsync({
                sku: item.sku,
                qty: 1,
                ...(replaces !== undefined ? { replacesLineId: replaces } : {}),
                idempotencyKey: intentKeys.keyFor(intent),
            });
            intentKeys.settle(intent);
            goToBuildSale();
        } catch (error) {
            // Solo un fallo pasajero reusa la llave; un rechazo del silo deja intentar de nuevo.
            if (!isTransientRequestError(error)) intentKeys.settle(intent);
            reportLineError(error, item, replaces);
        } finally {
            reservingRef.current = false;
        }
    };

    /** Mismo teléfono con el apartado vencido: lo renueva sin pedir otra unidad. */
    const renew = async (item: CatalogItem, lineId: string): Promise<void> => {
        if (renewingRef.current) return;
        renewingRef.current = true;
        setRenewing(true);
        try {
            await renewReservation.mutateAsync(lineId);
            goToBuildSale();
        } catch (error) {
            const fallback = renewFallbackOf(error);
            if (fallback === 'replace') await reserve(item, lineId);
            else if (fallback === 'add') {
                await refreshSession();
                await reserve(item);
            } else if (fallback === 'unavailable') toast.error(RENEW_UNAVAILABLE_MESSAGE);
            else reportLineError(error, item);
        } finally {
            renewingRef.current = false;
            setRenewing(false);
        }
    };

    /** «Elegir»: el mismo teléfono vigente avanza, vencido se renueva, otro se aparta o cambia. */
    const choose = async (
        item: CatalogItem,
        replacesLineId = session ? anchorLineOf(session)?.lineId : undefined,
    ): Promise<void> => {
        if (item.sku === sessionPhoneSku && sessionPhoneLine !== undefined) {
            if (isReservationLiveNow(sessionPhoneLine.expiresAt)) goToBuildSale();
            else await renew(item, sessionPhoneLine.lineId);
            return;
        }
        await reserve(item, replacesLineId);
    };

    // «Cambiar» lee la venta fresca: el teléfono que ya estaba lo puso otra pantalla.
    const swapInto = async (item: CatalogItem): Promise<void> => {
        const { data: fresh } = await refetchSession();
        const anchor = fresh ? anchorLineOf(fresh) : undefined;
        if (anchor === undefined) return;
        if (anchor.sku !== item.sku) await reserve(item, anchor.lineId);
        else if (isReservationLiveNow(anchor.expiresAt)) goToBuildSale();
        else await renew(item, anchor.lineId);
    };

    const toastAction = (action: LineChangeAction, item: CatalogItem) => ({
        label: LINE_CHANGE_ACTION_LABEL[action],
        onClick: () => {
            if (action === 'change-phone') void swapInto(item);
            else if (action === 'retry') void choose(item);
            else
                void navigate({
                    to: '/wizard/s/$sessionId/$step',
                    params: { sessionId, step: stepAfterCheckout(session) },
                });
        },
    });

    const creditBubblesOf = (item: CatalogItem): readonly BenefitBubble[] =>
        notEligible || !sureKeepFinances(promoItem(item))
            ? []
            : cardCreditBubbles(item, scopeWide, promoLevel, levelPending);

    const renderEntry = (entry: CatalogEntry): ReactNode => {
        if (entry.kind === 'item') {
            const benefits = slots.saleBenefits?.(promoItem(entry.item));
            return (
                <ProductCard
                    item={entry.item}
                    selected={entry.item.sku === pickedSku}
                    terms={terms}
                    onSelect={setTouchedSku}
                    creditTerms={creditTerms}
                    onShowKitContents={setKitContentsSku}
                    {...(benefits && { saleBenefits: benefits })}
                    creditBubbles={creditBubblesOf(entry.item)}
                />
            );
        }
        const variant = variantOf(entry.group);
        const benefits = slots.saleBenefits?.(promoItem(variant));
        return (
            <ModelCard
                group={entry.group}
                variant={variant}
                selected={entry.group.variants.some((v) => v.sku === pickedSku)}
                terms={terms}
                busy={busy}
                onSelect={(item) => {
                    setTouchedSku(item.sku);
                }}
                onVariantChange={(item) => {
                    changeVariant(entry.group, item);
                }}
                onChoose={(item) => {
                    changeVariant(entry.group, item);
                    void choose(item);
                }}
                creditTerms={creditTerms}
                {...(benefits && { saleBenefits: benefits })}
                creditBubbles={creditBubblesOf(variant)}
            />
        );
    };

    return (
        <div className="flex w-full max-w-6xl flex-col">
            <header className="mb-3 flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                        Selección
                    </div>
                    <h1 className="mb-1 flex items-center gap-2 text-[20px] font-semibold leading-[1.2] tracking-[-0.01em]">
                        Elige el teléfono
                        {sureKeep && session?.loan?.level !== undefined && (
                            <LevelChip level={session.loan.level} />
                        )}
                    </h1>
                    <p className="max-w-3xl text-[13px] leading-[1.5] text-muted-foreground">
                        Elige capacidad y color en cada tarjeta; toca el nombre para ver sus promos.
                    </p>
                </div>
                <div className="flex flex-none items-center gap-4 pt-1">
                    <WizardBackLink
                        sessionId={sessionId}
                        step={(session && previousStepSlug('product', session)) ?? 'score'}
                    />
                    <SaveAndExitButton />
                </div>
            </header>

            {session && slots.referredByNotice?.(customerFirstName(session))}

            <div className="mb-3 flex flex-wrap items-center gap-2">
                <div className="flex gap-1.5" role="group" aria-label="Tipo">
                    {PHONE_OFFER_FILTERS.map((f) => (
                        <button
                            key={f.value}
                            type="button"
                            aria-pressed={offerFilter === f.value}
                            onClick={() => {
                                setOfferFilter(f.value);
                            }}
                            className={cn(
                                'rounded-full border px-3 py-1 text-[12px] font-medium transition-colors',
                                offerFilter === f.value
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-border bg-card text-foreground hover:border-primary/50',
                            )}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto">
                    <PromoShortcutButton
                        sessionId={sessionId}
                        scopeWideCreditRows={
                            notEligible
                                ? []
                                : creditRows.filter((row) => row.reach.kind === 'store')
                        }
                    />
                </div>
            </div>

            <CatalogToolbar
                filters={filters}
                onChange={setFilters}
                brands={brands}
                financiers={financiers}
                shown={entries.length}
                total={totalEntries}
                searchRef={searchRef}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    {isLoading ? (
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                            {Array.from({ length: SKELETONS }, (_, i) => (
                                <ProductCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : isError ? (
                        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-10 text-center">
                            <PackageX className="size-7 text-muted-foreground" aria-hidden />
                            <p className="text-[13px] text-muted-foreground">
                                No se pudo leer el catálogo de la tienda.
                            </p>
                            <Button
                                size="sm"
                                variant="secondary"
                                isLoading={isFetching}
                                onClick={() => void refetch()}
                            >
                                Volver a intentar
                            </Button>
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-border py-10 text-center">
                            <SearchX className="size-7 text-muted-foreground" aria-hidden />
                            <p className="text-[13px] text-muted-foreground">
                                Ningún equipo coincide.
                            </p>
                        </div>
                    ) : (
                        <ul
                            aria-label="Equipos"
                            aria-busy={busy}
                            className={cn(
                                'grid auto-rows-fr grid-cols-2 gap-3 transition-opacity md:grid-cols-3',
                                busy && 'pointer-events-none opacity-60',
                            )}
                        >
                            {entries.map((entry) => (
                                <li key={entry.key} className="flex min-w-0">
                                    {renderEntry(entry)}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Una sola pieza sticky: el cuerpo hace scroll y el pie con «Elegir» queda visible. */}
                <section
                    aria-label="Detalle del equipo"
                    className="flex flex-col gap-3 lg:sticky lg:top-[4.25rem] lg:max-h-[calc(100dvh-5.25rem)] lg:self-start"
                >
                    <div className="flex min-h-0 flex-col gap-3 lg:flex-1 lg:overflow-y-auto">
                        <ProductDetailCard
                            picked={picked}
                            stock={picked?.stock ?? null}
                            reservation={
                                picked !== null && picked.sku === sessionPhoneSku ? 'held' : 'none'
                            }
                            imei={null}
                            clientLevel={promoLevel}
                            hideCreditPromotionLabel
                            {...(picked !== null &&
                                slots.detailPromotions && {
                                    salePromotions: (renderBonuses: RenderAccountBonuses) =>
                                        slots.detailPromotions?.(
                                            promoItem(picked),
                                            refreshContext,
                                            renderBonuses,
                                        ),
                                })}
                            discountedPriceCents={pickedDiscount}
                            downPayment={pickedDownPayment}
                            {...(picked !== null && {
                                creditPromotions: creditRowsFor(picked),
                                creditNote: notEligible
                                    ? 'notEligible'
                                    : levelPending
                                      ? 'levelPending'
                                      : undefined,
                            })}
                            {...(picked !== null &&
                                isPickedKit && {
                                    kitComponents: kitComponentCount(picked),
                                    onShowKitContents: () => {
                                        setKitContentsSku(picked.sku);
                                    },
                                })}
                        />
                    </div>
                    {picked !== null && (
                        <div className="flex flex-none flex-col gap-3">
                            <div className="flex items-baseline justify-between border-t border-border pt-2.5">
                                <span className="text-[12.5px] text-muted-foreground">
                                    Precio a pagar
                                </span>
                                <span className="flex flex-col items-end">
                                    <strong className="text-[20px] tabular-nums">
                                        {formatMXN(pickedPriceCents / 100)}
                                    </strong>
                                    {pickedCashPrice !== undefined && (
                                        <span
                                            data-testid="picked-cash-price"
                                            className="text-[11.5px] text-muted-foreground tabular-nums"
                                        >
                                            De contado {formatMXN(pickedCashPrice / 100)}
                                        </span>
                                    )}
                                </span>
                            </div>
                            <Button
                                isLoading={busy}
                                rightIcon={<ArrowRight className="size-4" aria-hidden />}
                                onClick={() => void choose(picked)}
                            >
                                Elegir
                            </Button>
                            {picked.sku !== sessionPhoneSku && (
                                <p className="text-center text-[11px] text-muted-foreground">
                                    Se aparta 30 min
                                </p>
                            )}
                        </div>
                    )}
                </section>
            </div>

            <KitContentsModal
                kit={kitInModal}
                items={items}
                discountedPriceCents={
                    kitInModal
                        ? (slots.saleBenefits?.(promoItem(kitInModal)).discountedPriceCents ?? null)
                        : null
                }
                busy={busy}
                onClose={() => {
                    setKitContentsSku(undefined);
                }}
                onChoose={() => {
                    if (kitInModal === null) return;
                    setTouchedSku(kitInModal.sku);
                    setKitContentsSku(undefined);
                    void choose(kitInModal);
                }}
            />
        </div>
    );
}
